import { type AudienceData, AudienceOption } from '@openx/types/targeting/audience';
import type { TargetingProps } from '@openx/types/targeting/targeting';
import { ComparisonType, Intersect } from '@openx/types/targeting/targetingValuesTypes';
import { noop } from '@openx/utils/noop';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext';
import isEqual from 'lodash/isEqual';
import { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CustomOptionsProvider } from '../../utils/hooks/useCustomOptionsContext';
import { EmptyFilterMessage } from '../shared/EmptyFilterMessage/EmptyFilterMessage';
import { usePreselectableDrawer } from '../shared/hooks/usePreselectableDrawer';
import { TargetingDrawer } from '../shared/TargetingDrawer/TargetingDrawer';
import { useDrawer } from '../shared/TargetingDrawer/useDrawer';
import { AudienceReadOnly } from './AudienceReadOnly';
import { AudienceTypeOption, audienceDefaultOptions } from './constants';
import { Items } from './Items';
import { TopBox } from './TopBox';

export const Audience = memo(function Audience({
  targetingParams,
  onFieldUpdate = noop,
  name,
  accountUid,
  readonly,
  field,
  customOptions,
  isFromCurated = false,
}: TargetingProps<AudienceData>) {
  const { t } = useTranslation();
  const { isOa } = usePermissionContext();
  const [userChangedInitialValue, setUserChangedInitialValue] = useState(false);

  const defaultType = useMemo(() => {
    if (customOptions?.defaultOption) {
      return customOptions.defaultOption;
    }

    if (isOa) {
      return AudienceOption.OPEN_AUDIENCES;
    }

    if (targetingParams) {
      if (targetingParams[AudienceOption.MATCHED_USERS]) {
        return AudienceOption.MATCHED_USERS;
      }

      const firstNonEidsKey = Object.keys(targetingParams).find(k => k !== AudienceOption.TRAFFIC_FILTER_EIDS);
      return (firstNonEidsKey ?? Object.keys(targetingParams)[0]) as AudienceOption;
    }

    return AudienceOption.OPEN_AUDIENCES;
  }, [customOptions?.defaultOption, isOa, targetingParams]);

  const defaultOperator = useMemo(
    () => (defaultType === AudienceOption.DMP_SEGMENTS ? ComparisonType.INTERSECTS : Intersect.INTERSECTS),
    [defaultType]
  );

  const intendedAudienceDefaultValue: AudienceData = useMemo(
    () => ({
      [AudienceOption.INTENDED_AUDIENCE]: { op: Intersect.INTERSECTS, val: AudienceTypeOption.Over_12 },
    }),
    []
  );

  const isIntendedAudienceEnabled = customOptions?.availableOptions.includes(AudienceOption.INTENDED_AUDIENCE);

  const initState: AudienceData | null = useMemo(() => {
    if (targetingParams) {
      const params = { ...targetingParams };
      const eids = params[AudienceOption.TRAFFIC_FILTER_EIDS];

      // special case for Eids: Sets are stored as Arrays (for dirty detection compatibility), convert back to Set here
      if (Array.isArray(eids)) params[AudienceOption.TRAFFIC_FILTER_EIDS] = new Set(eids);

      return params;
    }

    if (isIntendedAudienceEnabled) return intendedAudienceDefaultValue;

    return null;
  }, [targetingParams, isIntendedAudienceEnabled, intendedAudienceDefaultValue]);

  const [params, updateParams] = useState<AudienceData | null>(initState);
  const [type, setType] = useState<AudienceOption>(defaultType);

  const onFieldUpdateExtended = useCallback(
    (...args: Parameters<typeof onFieldUpdate>) => {
      setUserChangedInitialValue(true);
      onFieldUpdate(...args);
    },
    [onFieldUpdate]
  );

  const { isOpen, onDrawerClose, onDrawerOpen, onDrawerApply } = useDrawer({
    onApply: () => {
      const filteredParams = Object.keys(params ?? {}).reduce<Record<string, unknown>>((acc, key) => {
        const value: AudienceData[AudienceOption] = params?.[key];

        if (value instanceof Set) {
          // special case for Eids: convert to Array so RHF's deepEqual dirty detection works correctly
          acc[key] = Array.from(value);
        } else if (value) {
          acc[key] = value;
        }

        return acc;
      }, {});

      onFieldUpdateExtended(field, Object.keys(filteredParams).length ? filteredParams : null);
    },
    restoreParams: () => updateParams({ ...initState }),
  });

  const onTypeChange = (nextType: AudienceOption) => {
    const normalizedNextType =
      nextType === AudienceOption.TRAFFIC_FILTER_EIDS ? AudienceOption.MATCHED_USERS : nextType;

    setType(normalizedNextType);

    updateParams(prev => {
      if (!prev) return prev;

      if (normalizedNextType !== AudienceOption.INTENDED_AUDIENCE) {
        const intended = prev[AudienceOption.INTENDED_AUDIENCE];
        const selected = prev[normalizedNextType];
        const eids = prev[AudienceOption.TRAFFIC_FILTER_EIDS];

        const next: Partial<AudienceData> = {};

        if (intended) {
          next[AudienceOption.INTENDED_AUDIENCE] = intended;
        }

        if (selected) {
          (next as Record<string, unknown>)[normalizedNextType] = selected;
        }

        if (normalizedNextType === AudienceOption.MATCHED_USERS && eids) {
          (next as Record<string, unknown>)[AudienceOption.TRAFFIC_FILTER_EIDS] = eids;
        }

        return Object.keys(next).length ? (next as AudienceData) : null;
      }

      return prev[AudienceOption.INTENDED_AUDIENCE]
        ? { [AudienceOption.INTENDED_AUDIENCE]: prev[AudienceOption.INTENDED_AUDIENCE] }
        : null;
    });
  };

  const onRemoveAll = useCallback(() => {
    const initialValue: AudienceData | null = isIntendedAudienceEnabled ? intendedAudienceDefaultValue : null;

    onFieldUpdateExtended(field, initialValue);
    updateParams(initialValue);
  }, [isIntendedAudienceEnabled, intendedAudienceDefaultValue, field, onFieldUpdateExtended]);

  const onChange = useCallback(
    (val?: string | Set<string>, givenType?: AudienceOption) => {
      if (type !== AudienceOption.MATCHED_USERS && type !== AudienceOption.TRAFFIC_FILTER_EIDS) {
        updateParams(params => ({ ...params, [type]: { op: Intersect.INTERSECTS, val } }));
      } else {
        const targetKey =
          type === AudienceOption.TRAFFIC_FILTER_EIDS && !givenType
            ? AudienceOption.MATCHED_USERS
            : (givenType ?? type);

        updateParams(params => ({ ...params, [targetKey]: val }));
      }
    },
    [type]
  );

  const isValidFormat = (type: string): type is AudienceOption => {
    return Object.values(AudienceOption).includes(type as AudienceOption);
  };

  const openDrawerWithPreselectedType = usePreselectableDrawer<AudienceOption>(
    isValidFormat,
    onDrawerOpen,
    onTypeChange
  );

  const onChangeMulti = useCallback(
    (values: Set<string>) => {
      if (type !== AudienceOption.MATCHED_USERS && type !== AudienceOption.TRAFFIC_FILTER_EIDS) {
        updateParams({
          [type]: {
            ...(params?.[type] ? params?.[type] : { op: defaultOperator, val: null }),
            val: Array.from(values).join(','),
          },
        });
      }
    },
    [type, params, defaultOperator]
  );

  // for ALL is ComparisonType.INVERSE_IN, for ANY is ComparisonType.INTERSECTS, ANY is default
  const onAllAnyOperatorChange = useCallback(() => {
    const currentOperator = params?.[type];

    if (typeof currentOperator !== 'object' || !('op' in currentOperator)) {
      return;
    }

    const newOperator =
      currentOperator.op === ComparisonType.INTERSECTS ? ComparisonType.INVERSE_IN : ComparisonType.INTERSECTS;

    updateParams({ [type]: { ...currentOperator, op: newOperator } });
  }, [type, params]);

  const isEmpty = useMemo(() => Object.values(params ?? {}).every(value => !value), [params]);

  const isOnlyIntended = useMemo(
    () => Object.keys(params ?? {}).length === 1 && params?.[AudienceOption.INTENDED_AUDIENCE],
    [params]
  );

  const isDataUpdated = useMemo(() => {
    return !isEqual(targetingParams, params);
  }, [params, targetingParams]);

  return (
    <CustomOptionsProvider value={customOptions ?? audienceDefaultOptions}>
      <AudienceReadOnly
        name={name}
        field={field}
        targetingParams={initState}
        onOpenDrawerClick={openDrawerWithPreselectedType}
        onRemoveClick={isOnlyIntended ? undefined : onRemoveAll}
        accountUid={accountUid}
        readonly={readonly}
        isFromCurated={isFromCurated}
        userChangedInitialValue={userChangedInitialValue}
      />

      {isOpen && (
        <TargetingDrawer name={name} onClose={onDrawerClose} onApply={onDrawerApply} isApplyDisabled={!isDataUpdated}>
          <TopBox
            accountUid={accountUid as string}
            onChange={onChange}
            onChangeMulti={onChangeMulti}
            selectedType={type}
            onTypeChange={onTypeChange}
            params={params}
          />

          {isEmpty ? (
            <EmptyFilterMessage text={t('No filters applied.')} />
          ) : (
            <Items
              targetingParams={params}
              readonly={false}
              onChange={updateParams}
              accountUid={accountUid as string}
              onAllAnyOperatorChange={onAllAnyOperatorChange}
              isFromCurated={isFromCurated}
            />
          )}
        </TargetingDrawer>
      )}
    </CustomOptionsProvider>
  );
});
